/**
 * Copyright (c) S2 Partnership Ltd. 2022
 *
 * Entry point for the application. Sets up all the plugins and routing for the app.
 *
 * @summary Entry point for the application.
 * @author S2
 *
 * Created at     : 2021-01-11
 */

import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { AppRoutingModule } from './app-routing.module';
import { RootComponent } from './components/root/root.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpModule } from '../http/http.module';
import { EncryptionService } from './providers/encryption.service';
import { NetworkService } from './providers/network.service';
import { ProcessingOverlayService } from './providers/processing-overlay.service';
import { StorageService } from './providers/storage.service';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { DatePicker } from '@ionic-native/date-picker/ngx';
import { Chooser } from '@awesome-cordova-plugins/chooser/ngx';
import { FilePath } from '@awesome-cordova-plugins/file-path/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import localeEn from '@angular/common/locales/en';
import { UtilityService } from './providers/utility.service';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { AuthService } from './providers/auth.service';
import { PhotoViewer } from '@awesome-cordova-plugins/photo-viewer/ngx';
import { ConditionalLogicService } from './providers/conditional-logic.service';
import { BrandingService } from './providers/branding.service';
import { FormModule } from './../../modules/form/form.module';

registerLocaleData(localeEn, 'en');

/**
 * Method to load the local translation file.
 *
 * @arg {HttpClient} http The http client of the translation file on the app.
 * @return {TranslateHttpLoader} The translated json translation file.
 */
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
    declarations: [
        RootComponent
    ],
    exports: [],
    imports: [
        AppRoutingModule,
        BrowserModule,
        HttpModule,
        FormModule,
        IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        }),
    ],
    providers: [
        PhotoViewer,
        Camera,
        Chooser,
        FilePath,
        File,
        DatePicker,
        ScreenOrientation,
        EncryptionService,
        NetworkService,
        ProcessingOverlayService,
        StorageService,
        SplashScreen,
        StatusBar,
        UtilityService,
        AuthService,
        ConditionalLogicService,
        BrandingService,
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy }
    ],
    bootstrap: [RootComponent]
})
export class AppModule {}
